/**
 * frontend/src/pages/GoogleCallback.js
 * 
 * OAuth callback handler for Google authentication.
 * It receives the authorization code from Google, exchanges it for a token via the backend,
 * and completes the user login/registration process.
 * 
 * Common functions:
 * - handleCallback: Extracts the 'code' parameter from URL and calls loginWithGoogleCode.
 */
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { formatErrorMessage } from '../utils/errorHandling';
import BackgroundShapes from '../components/common/BackgroundShapes';
import '../styles/landing.css';

const GoogleCallback = () => {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState('');
  const { loginWithGoogleCode } = useAuth();
  const navigate = useNavigate();
  const processed = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      if (processed.current) return;
      processed.current = true;

      const code = searchParams.get('code');
      const errorParam = searchParams.get('error');

      if (errorParam) {
        setError('Google login was cancelled or failed. Please try again.');
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      if (!code) {
        setError('No authorization code received from Google.');
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      try {
        await loginWithGoogleCode(code);
        navigate('/dashboard');
      } catch (err) {
        setError(formatErrorMessage(err, 'Google login failed. Please try again.'));
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    handleCallback();
  }, [searchParams, loginWithGoogleCode, navigate]);

  return (
    <div className="auth-page">
      <BackgroundShapes />
      <Container className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="glass-card auth-card text-center">
          {error ? (
            <>
              <div className="text-danger mb-3">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="15" y1="9" x2="9" y2="15"></line>
                  <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>
              </div>
              <h3 className="mb-2">Login Failed</h3>
              <p className="text-muted">{error}</p>
              <p className="text-muted small">Redirecting to login...</p>
            </>
          ) : (
            <>
              <div className="spinner-border text-primary mb-3" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <h3 className="mb-2">Completing login...</h3>
              <p className="text-muted">Please wait while we sign you in with Google.</p>
            </>
          )}
        </div>
      </Container>
    </div>
  );
};

export default GoogleCallback;
